import { useState, useEffect, useRef } from 'react';
import { X, Crown, Trophy } from 'lucide-react';
import useGamificationStore from '../stores/useGamificationStore';
import { ACHIEVEMENTS } from '../utils/constants';
import './GamificationOverlay.css';

export default function GamificationOverlay() {
    const levelUpData = useGamificationStore(s => s.levelUpData);
    const dismissLevelUp = useGamificationStore(s => s.dismissLevelUp);
    const toastQueue = useGamificationStore(s => s.toastQueue || []);
    const popToast = useGamificationStore(s => s.popToast);
    const totalUnlocked = useGamificationStore(s => s.profile.unlockedAchievements?.length || 0);

    // ─── Achievement toasts ───
    const [displayToasts, setDisplayToasts] = useState([]);

    useEffect(() => {
        if (toastQueue.length > 0) {
            const newId = toastQueue[0];
            const achievement = ACHIEVEMENTS.find(a => a.id === newId);
            if (achievement) {
                setDisplayToasts(prev => [...prev, { ...achievement, _key: Date.now() }]);
            }
            popToast();
        }
    }, [toastQueue, popToast]);

    // Auto-dismiss achievements after 5s
    useEffect(() => {
        if (displayToasts.length > 0) {
            const timer = setTimeout(() => {
                setDisplayToasts(prev => prev.slice(1));
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [displayToasts]);

    // ─── Level-Up count-up ───
    const [countValue, setCountValue] = useState(null);

    useEffect(() => {
        if (!levelUpData) { setCountValue(null); return; }

        // Animate counter from old to new level
        const { oldLevel, newLevel } = levelUpData;
        setCountValue(oldLevel);

        const duration = 800;
        const steps = newLevel - oldLevel;
        const stepTime = duration / steps;
        let current = oldLevel;

        const interval = setInterval(() => {
            current++;
            setCountValue(current);
            if (current >= newLevel) clearInterval(interval);
        }, stepTime);

        return () => clearInterval(interval);
    }, [levelUpData]);

    return (
        <div className="gamification-overlay">
            {/* ═══ Level Up Modal ═══ */}
            {levelUpData && (
                <div className="gamification-modal-overlay">
                    {/* CSS Confetti Burst */}
                    <div className="confetti-container" aria-hidden="true">
                        {Array.from({ length: 30 }).map((_, i) => (
                            <div
                                key={i}
                                className="confetti-particle"
                                style={{
                                    '--angle': `${(i / 30) * 360}deg`,
                                    '--distance': `${80 + Math.random() * 120}px`,
                                    '--delay': `${Math.random() * 0.3}s`,
                                    '--color': CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                                    '--size': `${4 + Math.random() * 4}px`,
                                    '--drift': `${-30 + Math.random() * 60}deg`,
                                }}
                            />
                        ))}
                    </div>

                    <div className="level-up-card animate-slide-up">
                        <button className="btn btn-ghost btn-icon level-up-close" onClick={dismissLevelUp}>
                            <X size={20} />
                        </button>

                        {/* Gradient top bar */}
                        <div className="level-up-topbar" />

                        <div className="level-up-content">
                            {/* Glow ring */}
                            <div className="level-up-glow" />

                            <div className="level-up-icon-wrapper">
                                <Crown size={48} className="level-up-icon" />
                            </div>

                            <h2 className="level-up-title">Level Up!</h2>

                            {/* Animated counter badge */}
                            <div className="level-up-badge">
                                <span className="level-up-number" key={countValue}>
                                    {countValue ?? levelUpData.newLevel}
                                </span>
                            </div>

                            <h3 className="level-up-name">{levelUpData.name}</h3>

                            <p className="level-up-desc">
                                {getLevelUpMessage(levelUpData.newLevel)}
                            </p>

                            <button className="btn btn-primary btn-lg level-up-btn" onClick={dismissLevelUp}>
                                Let's Go! 🚀
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Achievement Toasts ═══ */}
            <div className="achievement-toast-container">
                {displayToasts.map((achievement, index) => (
                    <div key={achievement._key || `${achievement.id}-${index}`} className="achievement-toast">
                        <div className="achievement-toast-icon">{achievement.icon}</div>
                        <div className="achievement-toast-content">
                            <h4>Achievement Unlocked!</h4>
                            <p>{achievement.name}</p>
                            <span className="achievement-toast-progress">
                                {totalUnlocked} of {ACHIEVEMENTS.length} unlocked
                            </span>
                        </div>
                        {/* Auto-dismiss progress bar */}
                        <div className="achievement-toast-timer" />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Helpers ───

const CONFETTI_COLORS = [
    'var(--brand-400)', 'var(--indigo-500)', '#EC4899', 'var(--caution)',
    'var(--positive)', 'var(--highlight-cyan)', '#EF4444', 'var(--brand-500)',
];

function getLevelUpMessage(level) {
    if (level <= 3) return 'Your journey is just beginning. Keep building momentum!';
    if (level <= 6) return 'You\'re developing real discipline. The Scholar tier awaits!';
    if (level <= 10) return 'Your consistency is paying off. You\'re becoming a master!';
    if (level <= 15) return 'Few reach this far. You are truly exceptional!';
    return 'You\'ve transcended ordinary limits. Legendary status achieved!';
}
